/**
 * 16 PDMODE marker geometry prototypes (Q10.A).
 *
 * # PDMODE bitset semantics (AutoCAD)
 *
 *   Low nibble (0..4): base marker shape
 *     0 = single dot (single pixel)
 *     1 = invisible (no marker drawn)
 *     2 = + (cross)
 *     3 = X
 *     4 = vertical tick
 *
 *   High bits (32 / 64 / 96): outline modifier (combinable)
 *     +32  → inscribed circle
 *     +64  → inscribed square
 *     +96  → inscribed circle + square (32 | 64)
 *
 * Combinations: PDMODE = base | modifier. e.g. PDMODE 34 = +cross
 * inscribed in a circle. PDMODE 35 = X inscribed in a circle. PDMODE
 * 36 = tick inscribed in a circle. The full 16-prototype set covers
 * every base × modifier pair AutoCAD draws.
 *
 * # PDSIZE semantics
 *
 *   PDSIZE = 0  → marker drawn at 5% of viewport height (screen-relative)
 *   PDSIZE > 0  → marker drawn at PDSIZE absolute *world* units
 *   PDSIZE < 0  → marker drawn at |PDSIZE|% of viewport height (screen-relative)
 *
 * # What this module emits
 *
 * Each prototype is a list of 2D segments (`{ a, b }`) drawn in a
 * unit-radius local frame. The renderer translates by `position`,
 * scales by the resolved PDSIZE, and emits the segments into the
 * batched line mesh.
 *
 * The intent is to keep the PDMODE math out of the shader path —
 * marker prototypes are baked once at module load and re-used for
 * every POINT entity.
 */

/** A single 2D line segment in unit-radius marker space. */
export interface MarkerSegment {
  ax: number;
  ay: number;
  bx: number;
  by: number;
}

/** A single 2D circle in unit-radius marker space (centre, radius). */
export interface MarkerCircle {
  cx: number;
  cy: number;
  r: number;
}

/** A complete marker prototype: zero or more segments + optional circle. */
export interface MarkerPrototype {
  /** PDMODE bitset value this prototype satisfies. */
  pdmode: number;
  /** Line segments in unit-radius local space. */
  segments: MarkerSegment[];
  /** Optional inscribed circles (PDMODE +32). */
  circles: MarkerCircle[];
  /** Optional inscribed squares (PDMODE +64). */
  squares: MarkerSegment[][];
  /**
   * Whether the marker is drawn at all. PDMODE 1 (invisible) sets this
   * to `false`; everything else `true`.
   */
  visible: boolean;
}

const UNIT = 1;
const HALF = 0.5;

// ── Base markers (no outline modifier) ─────────────────────────────

/** Single dot — drawn as zero-length line at origin. */
function makeDotSegments(): MarkerSegment[] {
  return [{ ax: 0, ay: 0, bx: 0, by: 0 }];
}

/** + cross. */
function makeCrossSegments(): MarkerSegment[] {
  return [
    { ax: -UNIT, ay: 0, bx: UNIT, by: 0 },
    { ax: 0, ay: -UNIT, bx: 0, by: UNIT },
  ];
}

/** X. */
function makeXSegments(): MarkerSegment[] {
  return [
    { ax: -UNIT, ay: -UNIT, bx: UNIT, by: UNIT },
    { ax: -UNIT, ay: UNIT, bx: UNIT, by: -UNIT },
  ];
}

/** Vertical tick (above the point). */
function makeTickSegments(): MarkerSegment[] {
  return [{ ax: 0, ay: 0, bx: 0, by: UNIT }];
}

// ── Outline modifiers ──────────────────────────────────────────────

function makeInscribedCircle(): MarkerCircle {
  return { cx: 0, cy: 0, r: UNIT };
}

/** Inscribed square: 4 segments. */
function makeInscribedSquareSegments(): MarkerSegment[] {
  return [
    { ax: -HALF, ay: -HALF, bx: HALF, by: -HALF },
    { ax: HALF, ay: -HALF, bx: HALF, by: HALF },
    { ax: HALF, ay: HALF, bx: -HALF, by: HALF },
    { ax: -HALF, ay: HALF, bx: -HALF, by: -HALF },
  ];
}

/**
 * Build the complete prototype for a given PDMODE bitset value. Used
 * at module load to populate the lookup table; can also be called
 * directly when the renderer needs a prototype for an unrecognised
 * bitset (custom PDMODE blends).
 */
export function buildMarkerPrototype(pdmode: number): MarkerPrototype {
  const base = pdmode & 0b00111;
  const modifier = pdmode & 0b11100000;

  let visible = true;
  let segments: MarkerSegment[] = [];

  switch (base) {
    case 0:
      segments = makeDotSegments();
      break;
    case 1:
      visible = false;
      break;
    case 2:
      segments = makeCrossSegments();
      break;
    case 3:
      segments = makeXSegments();
      break;
    case 4:
      segments = makeTickSegments();
      break;
    default:
      // Unknown base — treat as dot.
      segments = makeDotSegments();
      break;
  }

  const circles: MarkerCircle[] = [];
  const squares: MarkerSegment[][] = [];
  if ((modifier & 32) === 32) circles.push(makeInscribedCircle());
  if ((modifier & 64) === 64) squares.push(makeInscribedSquareSegments());

  return { pdmode, segments, circles, squares, visible };
}

/**
 * Pre-built prototype lookup for the 16 standard PDMODE values
 * AutoCAD draws (0..4, 32..36, 64..68, 96..100). The renderer hits
 * this map first and falls back to `buildMarkerPrototype` for custom
 * blends.
 */
export const POINT_MARKER_PROTOTYPES: ReadonlyMap<number, MarkerPrototype> =
  (() => {
    const map = new Map<number, MarkerPrototype>();
    for (const base of [0, 1, 2, 3, 4]) {
      for (const modifier of [0, 32, 64, 96]) {
        const pdmode = base | modifier;
        map.set(pdmode, buildMarkerPrototype(pdmode));
      }
    }
    return map;
  })();

/**
 * Resolve a PDMODE bitset to its prototype, falling back to
 * `buildMarkerPrototype` for un-cached values. Returns the dot
 * prototype for any input that fails to parse.
 */
export function getMarkerPrototype(pdmode: number): MarkerPrototype {
  const cached = POINT_MARKER_PROTOTYPES.get(pdmode);
  if (cached) return cached;
  return buildMarkerPrototype(pdmode);
}

/**
 * Compute the effective marker world-radius given PDSIZE and the
 * current viewport height in world units.
 *
 *   PDSIZE = 0  → 5% of viewport height (screen-relative)
 *   PDSIZE > 0  → PDSIZE absolute world units
 *   PDSIZE < 0  → |PDSIZE|% of viewport height (screen-relative)
 */
export function effectiveMarkerRadius(
  pdsize: number,
  viewportHeightWorld: number,
): number {
  if (pdsize === 0) return viewportHeightWorld * 0.025;
  if (pdsize > 0) return pdsize / 2;
  return (viewportHeightWorld * Math.abs(pdsize)) / 200;
}
